import { useState, useEffect, useCallback, useRef } from 'react';

interface UseTimerProps {
  initialMinutes: number;
  onTimeUp?: () => void;
}

interface UseTimerReturn {
  timeLeft: number;
  hours: number;
  minutes: number;
  seconds: number;
  isWarning: boolean;
  formattedTime: string;
  isPaused: boolean;
  pause: () => void;
  resume: () => void;
  restart: () => void;
}

// Función auxiliar para formatear el tiempo en HH:MM:SS
function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  const parts = [];
  if (hours > 0) parts.push(hours.toString().padStart(2, '0'));
  parts.push(minutes.toString().padStart(2, '0'));
  parts.push(seconds.toString().padStart(2, '0'));

  return parts.join(':');
}

export const useTimer = ({ initialMinutes, onTimeUp }: UseTimerProps): UseTimerReturn => {
  const [timeLeft, setTimeLeft] = useState(initialMinutes * 60);
  const [isPaused, setIsPaused] = useState(false);
  const initialTimeRef = useRef(initialMinutes * 60);

  useEffect(() => {
    setTimeLeft(initialMinutes * 60);
  }, [initialMinutes]);

  // Efecto para manejar el temporizador
  useEffect(() => {
    if (isPaused || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          onTimeUp?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isPaused, timeLeft, onTimeUp]);

  // Funciones de control
  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);
  const restart = useCallback(() => {
    setTimeLeft(initialTimeRef.current);
    setIsPaused(false);
  }, []);

  // Cálculos derivados
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;
  const isWarning = timeLeft <= 300; // 5 minutos o menos

  return {
    timeLeft,
    hours,
    minutes,
    seconds,
    isWarning,
    formattedTime: formatTime(timeLeft),
    isPaused,
    pause,
    resume,
    restart,
  };
};
